import React from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { styled, useTheme } from "@mui/material/styles";
import REST from "../../libs/API";
import PSIcon from "../../components/PSIcon";
import { CopyIcon, QRCodeIcon, Warning } from "../../assets/images/icons";
import CClipboard from '../../components/CClipboard';
import Notice from '../main/Notice';
import { getText } from '../../libs/utils';
import TimerTile from './TimerTile';
import NetworkLoad from '../../components/NetworkLoad';
import UniModal from '../../components/UniModal';
import QRCode from 'react-qr-code';
import CancelOrder from './CancelOrder';

const MyBox = styled(Box)(({ theme }) => ({
  boxShadow: '0px 5px 6px -7px rgba(0, 0, 0, 0.35), 0px 4px 14px -3px rgba(0, 0, 0, 0.15)',
  borderRadius: 36,
  display: 'flex',
  margin: '40px 0',
  padding: '22px 28px 21px 28px',
  [theme.breakpoints.only('md')]: {
    padding: '16px 20px 15px',
    borderRadius: '28px',
  },
  [theme.breakpoints.only('sm')]: {
    padding: '21px 20px 20px',
    borderRadius: '28px',
    margin: '40px 0 25px',
  },
  [theme.breakpoints.only('xs')]: {
    padding: '21px 20px 20px',
    borderRadius: '28px',
    margin: '25px 0',
  }
}))

const MyBottomInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.only('xs')]: {
    flexDirection: 'column',
    alignItems: 'end'
  }
}))

const MemoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: '0 25px 0 0',
  [theme.breakpoints.only('xs')]: {
    margin: '0'
  }
}))

const BottomAction = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  marginTop: '20px',
  flexWrap: 'wrap',
  [theme.breakpoints.only('xs')]: {
    gap: '15px',
    justifyContent: 'space-between',
    " > .MuiButton-root": {
      width: '100%',
    },
    " > .MuiBox-root": {
      paddingLeft: 0
    }
  }
}))

const PayByCrypto = ({ order, getStatus, handleTimeOut }) => {
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));

  const setPaid = () => {
    REST.setPaid(order.code).then(data => {
      if (data.success) {
        getStatus();
      } else {
        window.showMessage(data.data[0].message);
      }
    });
  }

  const showDialog = () => {
    if (order?.toPaymentSystemType?.value == "BANK") {
      window.showModal({
        header: getText('dialKeepOrderHeader'),
        content: getText('dialKeepOrderText'),
        button: getText('dialKeepOrderButton'),
        onAccept: () => {
          setPaid();
        },
      });
    } else {
      setPaid();
    }
  }

  const networkInfo = order.fromNetworkInfo;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h3">{getText('orderHeaderState9')}</Typography>
        <TimerTile timerEnd={order.endAt} handleTimeOut={handleTimeOut} />
      </Box>

      <Typography variant="regularCaption" component="div">
        {getText('payCryptoTimerHint')}
      </Typography>

      <MyBox>
        <PSIcon
          name={order.fromPaymentSystemClassName}
          network={order.fromCurrencyNetwork}
          height={isXS ? 35 : 45}
          width={isXS ? 35 : 45}
          margin={isXS ? '5px 15px 0 0' : '10px 20px 0 0'}
        />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', margin: { xs: '0 0 11px', sm: '0 0 3px' } }}>
            <Typography variant='orderHash'>
              {order.directReqs.number.value}
            </Typography>
            <Box sx={{
              margin: { xs: '2px 0 0 10px', sm: '6px 0 0 10px' },
              display: 'flex',
              gap: '12px',
              flexDirection: { xs: 'column', sm: 'row' },
            }}>
              <CClipboard text={order.directReqs.number.copyValue}>
                <CopyIcon width={isXS ? 20 : 32} height={isXS ? 20 : 32} />
              </CClipboard>
              <UniModal initElement={QRCodeIcon} initElementProps={{ height: isXS ? 20 : 32, width: isXS ? 20 : 32 }}>
                <QRCode size={190} value={order.directReqs.number.copyValue} />
                <Typography variant="p3" sx={{
                  wordBreak: 'break-all',
                }}>{order.directReqs.number.copyValue}</Typography>
              </UniModal>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'space-between' } }}>

            {!isXS && <Typography variant='regularCaption'>{order.fromPaymentSystem}</Typography>}

            <MyBottomInfo>
              {(order.directReqs.paymentNumber && order.directReqs.paymentNumber.label !== 'Payment ID') &&
                <MemoBox>
                  <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, textTransform: 'uppercase' }}>
                    {order.directReqs.paymentNumber.label}: {order.directReqs.paymentNumber.value}
                  </Typography>
                  <CClipboard text={order.directReqs.paymentNumber.copyValue}>
                    <Box sx={{ margin: '4px 0 0 9px' }}><CopyIcon width={isXS ? 20 : 24} height={isXS ? 20 : 24} /></Box>
                  </CClipboard>
                </MemoBox>
              }

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 } }}>{order.amount?.value} {order.amount?.currencyCode}</Typography>
                <CClipboard text={order.amount?.value}>
                  <Box sx={{ margin: '4px 0 0 9px' }}><CopyIcon width={isXS ? 20 : 24} height={isXS ? 20 : 24} /></Box>
                </CClipboard>
              </Box>
            </MyBottomInfo>
          </Box>
        </Box>
      </MyBox>

      <Notice type="error">
        <p>{getText('scamWarnText01')} {getText('scamWarnText02')}</p>
      </Notice>

      {Object.keys(order).length > 0 && order.deposits[0].tabs[0].message && (
        <Notice
          type="warning"
          icon={<Warning />}
          style={{ margin: '21px 0 20px' }}
        >
          <p>{order.deposits[0].tabs[0].message}</p>
        </Notice>
      )}

      <BottomAction>

        {!isXS && <Button onClick={() => showDialog()} variant="contained" sx={{ padding: '15px 31px' }}>{getText('sSubmitPaid')}</Button>}
        {!isXS && <CancelOrder size="large" orderCode={order.code} updateCallback={getStatus} />}
        {networkInfo?.recommendedFee &&
          <Box sx={{ paddingLeft: '17px' }}>
            <Box sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 14 }}>{networkInfo.recommendedFee}</Box>
            <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText("networkRecomendedFee")}</Box>
          </Box>
        }

        {networkInfo?.load &&
          <NetworkLoad order={order} />
        }

        {isXS && <Button onClick={() => showDialog()} variant="contained" sx={{ padding: '15px 31px' }}>{getText('sSubmitPaid')}</Button>}
        {isXS && <CancelOrder size="large" orderCode={order.code} updateCallback={getStatus} />}
      </BottomAction>
    </>
  )
}

export default PayByCrypto